"use client";
import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "@zapier/i18n";
import { Avatar, Key, Text } from "@zapier/design-system";
import type { Member } from "@zapier/bff";
import {
  useCurrentUser,
  useMembers,
  useQuerySearchMembers,
} from "@zapier/client";
import { DEFAULT_FOLDER_ID, getFolderUrl } from "@zapier/utils";
import type { TypeaheadItem } from "../FilterTypeahead";

import { FilterTypeahead } from "../FilterTypeahead";
import { useRouterParams } from "../../../hooks/useRouterParams";

import styles from "./MemberFolderLookup.module.css";

// getFolderUrl(DEFAULT_FOLDER_ID) with no memberId returns the bare pathname
// (no query string) — the canonical Personal-folder route everything else
// in the app uses, so this can't drift out of sync with it.
const PERSONAL_FOLDER_PATHNAME = getFolderUrl(DEFAULT_FOLDER_ID);

function MemberFolderLookup() {
  const t = useTranslations("member-folder-lookup");
  const { user } = useCurrentUser();
  const { memberId, setParams } = useRouterParams();

  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedKey, setSelectedKey] = useState<Key>(memberId || "");

  // Keep the displayed selection in sync with the URL — covers browser
  // back/forward and direct links to someone else's Personal folder.
  useEffect(() => {
    setSelectedKey(memberId || "");
  }, [memberId]);

  const { members } = useQuerySearchMembers({
    search: searchValue,
    enabled: true,
  });

  // The URL-selected member may not be in the default (empty-search) result
  // page, so fetch them directly and prepend below if missing.
  const { members: membersById } = useMembers({
    ids: !searchValue && memberId ? [Number(memberId)] : [],
  });

  const myId = user?.id;

  const items = useMemo(() => {
    const ownerSuggestions = members
      .filter((member) => member.id !== myId)
      .map((member) => ({ ...member, name: member.fullName }));

    if (
      memberId &&
      membersById &&
      !searchValue &&
      !ownerSuggestions.some(({ id }) => String(id) === memberId)
    ) {
      const member = membersById[Number(memberId)];
      if (member) {
        ownerSuggestions.unshift({ ...member, name: member.fullName });
      }
    }

    return ownerSuggestions;
  }, [members, myId, memberId, membersById, searchValue]);

  const selectKey = (key: Key | null) => {
    setSelectedKey(key || "");
    setSearchValue("");

    if (!key) {
      setParams({ page: 1 }, ["memberId"], PERSONAL_FOLDER_PATHNAME);
      return;
    }

    setParams({ memberId: key, page: 1 }, [], PERSONAL_FOLDER_PATHNAME);
  };

  return (
    <FilterTypeahead
      isLabelHidden
      items={items}
      label={t("labels.title")}
      menuWidth="22ch"
      onSearchChange={setSearchValue}
      onSelectKey={selectKey}
      placeholder={t("placeholders.search")}
      selectedKey={selectedKey}
      renderItem={(item) => {
        const member = item as TypeaheadItem & Member;
        return (
          <div className={styles.menuItem}>
            <Avatar url={member.photoUrl} name={member.fullName} />
            <Text tag="p">{member.name}</Text>
          </div>
        );
      }}
    />
  );
}

export default MemberFolderLookup;
